import { findSeatTransfer } from '@val/engine'
import { getParty } from '@val/shared'
import { useMemo, useState } from 'react'
import { aleOpts } from '../lib/config.ts'
import { int, pct2 } from '../lib/format.ts'
import './sim.css'

interface Props {
  votes: Record<string, number>
  validVotes: number
  highlightParty?: string
}

export function SeatTransferTool({ votes, validVotes, highlightParty }: Props) {
  const codes = Object.keys(votes)
  const [gainer, setGainer] = useState(highlightParty ?? codes[0] ?? 'C')
  const [loser, setLoser] = useState(codes.find((c) => c !== gainer) ?? 'KD')

  const result = useMemo(() => {
    if (gainer === loser) return null
    try {
      const input = codes.map((party) => ({ party, votes: votes[party] ?? 0 }))
      return findSeatTransfer(input, aleOpts(validVotes), gainer, loser)
    } catch {
      return null
    }
  }, [gainer, loser, votes, validVotes, codes])

  const votePct = (n: number) => (validVotes > 0 ? pct2((n / validVotes) * 100) : '–')

  return (
    <div className="transfer">
      <div className="transfer__controls">
        <label className="field">
          <span>Parti som vinner mandat</span>
          <select value={gainer} onChange={(e) => setGainer(e.target.value)}>
            {codes.map((c) => (
              <option key={c} value={c}>
                {getParty(c).shortName} – {getParty(c).name}
              </option>
            ))}
          </select>
        </label>
        <span className="transfer__arrow" aria-hidden>
          ←
        </span>
        <label className="field">
          <span>Parti som tappar mandat</span>
          <select value={loser} onChange={(e) => setLoser(e.target.value)}>
            {codes.map((c) => (
              <option key={c} value={c}>
                {getParty(c).shortName} – {getParty(c).name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {gainer === loser ? (
        <p className="dim">Välj två olika partier.</p>
      ) : !result?.feasible ? (
        <p className="notice">
          Ett sådant mandatbyte går inte att nå med rimliga röstförändringar utifrån det här läget.
        </p>
      ) : (
        <div className="transfer__result">
          <p>
            För att <strong>{getParty(gainer).shortName}</strong> ska ta ett mandat från{' '}
            <strong>{getParty(loser).shortName}</strong>:
          </p>
          <ul>
            <li>
              {Number.isFinite(result.votesNeeded) ? (
                <>
                  <strong>+{int(result.votesNeeded)} röster</strong> till{' '}
                  {getParty(gainer).shortName}{' '}
                  <span className="dim">({votePct(result.votesNeeded)} av rösterna)</span>, allt
                  annat lika
                </>
              ) : (
                <span className="dim">
                  Går inte enbart genom fler röster till {getParty(gainer).shortName}.
                </span>
              )}
            </li>
            <li>
              {Number.isFinite(result.votesSwitchedIfFromLoser) ? (
                <>
                  eller <strong>{int(result.votesSwitchedIfFromLoser)} röster</strong> som flyttas
                  direkt {getParty(loser).shortName} → {getParty(gainer).shortName}{' '}
                  <span className="dim">({votePct(result.votesSwitchedIfFromLoser)})</span>
                </>
              ) : (
                <span className="dim">Ett direkt röstbyte räcker inte inom sökintervallet.</span>
              )}
            </li>
          </ul>
        </div>
      )}
    </div>
  )
}
